import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewEncapsulation,
  signal,
} from "@angular/core";
import * as Highcharts from "highcharts/highstock";
import {
  HighchartsChartComponent,
  providePartialHighcharts,
} from "highcharts-angular";
import { HC_RANGE_SELECTOR } from "../../../charts/highcharts-shared";
import moment from "moment";

interface ChartSeriesEntry {
  name: string;
  data: number[][];
  type: "area" | "line";
  color?: string;
  dashStyle?: string;
  fillColor?: any;
  zIndex?: number;
  marker?: any;
  lineWidth?: number;
}

@Component({
  selector: "app-exp-cumulative-return-chart",
  standalone: true,
  imports: [HighchartsChartComponent],
  encapsulation: ViewEncapsulation.None,
  providers: [
    providePartialHighcharts({
      modules: () => [
        import("highcharts/esm/modules/stock"),
        import("highcharts/esm/modules/series-label"),
      ],
    }),
  ],
  template: `
    <div class="exp-cum-chart" #host>
      @if (loaded()) {
        <highcharts-chart
          [options]="chartOptions"
          [constructorType]="'stockChart'"
          (chartInstance)="setChartInstance($event)"
          style="width:100%; height:100%; display:block;"
        ></highcharts-chart>
      }
    </div>
  `,
  styles: [
    `
      .exp-cum-chart {
        width: 100%;
        min-height: 380px;
        position: relative;
      }
      .exp-cum-chart .highcharts-background {
        fill: transparent !important;
      }
      .exp-cum-chart .highcharts-plot-background {
        fill: transparent !important;
      }
      .exp-cum-chart .highcharts-grid .highcharts-grid-line {
        stroke: var(--border);
        stroke-dasharray: 2 3;
        opacity: 0.7;
      }
      .exp-cum-chart .highcharts-axis-line {
        stroke: var(--border) !important;
      }
      .exp-cum-chart .highcharts-tick {
        stroke: var(--border) !important;
      }
      .exp-cum-chart .highcharts-axis-labels text,
      .exp-cum-chart .highcharts-axis-title,
      .exp-cum-chart .highcharts-yaxis-labels text,
      .exp-cum-chart .highcharts-xaxis-labels text {
        fill: var(--text-dim) !important;
        font-family: var(--font-ui) !important;
        font-size: 11px !important;
        font-weight: 500 !important;
      }
      .exp-cum-chart .highcharts-legend-item text {
        fill: var(--text-dim) !important;
        font-family: var(--font-ui) !important;
        font-weight: 500 !important;
        font-size: 11.5px !important;
      }
      .exp-cum-chart .highcharts-legend-item:hover text {
        fill: var(--text) !important;
      }
      .exp-cum-chart
        .highcharts-legend-item.highcharts-legend-item-hidden
        text {
        fill: var(--text-faint) !important;
      }
      .exp-cum-chart .highcharts-range-selector-group text {
        font-family: var(--font-ui) !important;
        font-weight: 500 !important;
        font-size: 11.5px !important;
      }
      .exp-cum-chart .highcharts-range-label,
      .exp-cum-chart .highcharts-range-input {
        display: none !important;
      }
      .exp-cum-chart .highcharts-range-selector-buttons .highcharts-button {
        fill: transparent !important;
        stroke: transparent !important;
      }
      .exp-cum-chart
        .highcharts-range-selector-buttons
        .highcharts-button
        .highcharts-button-box {
        fill: var(--surface-2) !important;
        stroke: var(--border) !important;
        stroke-width: 1 !important;
        rx: 999;
        ry: 999;
        transition:
          fill 0.12s,
          stroke 0.12s;
      }
      .exp-cum-chart
        .highcharts-range-selector-buttons
        .highcharts-button
        text {
        fill: var(--text-dim) !important;
        transition: fill 0.12s;
      }
      .exp-cum-chart
        .highcharts-range-selector-buttons
        .highcharts-button-hover
        .highcharts-button-box {
        fill: var(--surface-2) !important;
        stroke: var(--border-strong) !important;
      }
      .exp-cum-chart
        .highcharts-range-selector-buttons
        .highcharts-button-hover
        text {
        fill: var(--text) !important;
      }
      .exp-cum-chart
        .highcharts-range-selector-buttons
        .highcharts-button-pressed
        .highcharts-button-box {
        fill: var(--accent-soft) !important;
        stroke: rgba(124, 92, 255, 0.35) !important;
      }
      .exp-cum-chart
        .highcharts-range-selector-buttons
        .highcharts-button-pressed
        text {
        fill: var(--accent-2) !important;
        font-weight: 600 !important;
      }
      .exp-cum-chart .highcharts-tooltip-box {
        fill: var(--surface-2) !important;
        stroke: var(--border-strong) !important;
        stroke-width: 1 !important;
        fill-opacity: 1 !important;
      }
      .exp-cum-chart .highcharts-tooltip text {
        fill: var(--text) !important;
      }
      .exp-cum-chart .highcharts-input-group {
        display: none;
      }
      .exp-cum-chart .highcharts-credits {
        display: none;
      }
      .exp-cum-chart .highcharts-plot-band {
        fill: var(--surface-2) !important;
      }
    `,
  ],
})
export class ExpCumulativeReturnChartComponent implements OnInit, OnChanges {
  @Input() plotData: any = null;
  @Input() benchmark: any = null;
  @Input() height: string | number = 380;
  @Input() chartStart: any = null;
  @Input() chartEnd: any = null;
  @Input() highlightStart: any = null;
  @Input() highlightEnd: any = null;
  @Input() startDate: any = null;
  @Input() endDate: any = null;
  @Input() isPrint = false;

  loaded = signal(false);
  chartOptions: Highcharts.Options = {};
  chartInstance: Highcharts.Chart | null = null;

  private series: ChartSeriesEntry[] = [];
  private dataStart: number | null = null;
  private dataEnd: number | null = null;
  private originalSeriesData: number[][][] = [];
  private isRebasing = false;

  private readonly portfolioColor = "#7C5CFF";
  private readonly benchmarkColor = "#54C7FF";

  constructor(
    private host: ElementRef<HTMLElement>,
  ) {}

  ngOnInit(): void {
    this.initPlot();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.plotData || changes.benchmark) {
      this.loaded.set(false);
      this.initPlot();
    }
    if (changes.startDate || changes.endDate) {
      this.updateDates();
    }
  }

  setChartInstance(chart: Highcharts.Chart): void {
    this.chartInstance = chart;
  }

  private updateDates(): void {
    if (!this.chartInstance) return;
    const axis: any = this.chartInstance.xAxis[0];
    const start = this.startDate
      ? moment(this.startDate).unix() * 1000
      : axis.dataMin;
    const end = this.endDate
      ? moment(this.endDate).unix() * 1000
      : axis.dataMax;
    axis.setExtremes(start, end);
  }

  private initPlot(): void {
    if (!this.plotData?.cumulative_returns) return;
    const portfolioRaw = this.plotData.cumulative_returns.Portfolio;
    if (!Array.isArray(portfolioRaw) || portfolioRaw.length < 2) return;

    // Normalize portfolio to start at 0% regardless of input scale (raw [1..2] or [100..200]).
    const baseValue = portfolioRaw[0][1];
    const portfolioData: number[][] = baseValue
      ? portfolioRaw.map((d: any) => [
          d[0],
          +((d[1] / baseValue - 1) * 100).toFixed(4),
        ])
      : portfolioRaw.map((d: any) => [d[0], d[1]]);

    this.dataStart = portfolioData[0][0];
    this.dataEnd = portfolioData[portfolioData.length - 1][0];

    const portfolioSeries: ChartSeriesEntry = {
      name: "Portfolio",
      data: portfolioData,
      type: "area",
      color: this.portfolioColor,
      lineWidth: 2,
      zIndex: 2,
      fillColor: {
        linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
        stops: [
          [0, "rgba(124,92,255,0.45)"],
          [1, "rgba(124,92,255,0.02)"],
        ],
      },
      marker: { enabled: false },
    };

    this.series = [portfolioSeries];

    const benchmarkSeries = this.buildBenchmarkSeries(portfolioData);
    if (benchmarkSeries) this.series.push(benchmarkSeries);

    this.originalSeriesData = this.series.map((s) => s.data.map((d) => [...d]));
    this.initChart();
  }

  private buildBenchmarkSeries(
    _portfolioData: number[][],
  ): ChartSeriesEntry | null {
    if (
      !this.benchmark?.cumulative_returns ||
      !Array.isArray(this.benchmark.cumulative_returns)
    )
      return null;

    let conv: number[][] = this.benchmark.cumulative_returns
      .map((d: any) => [this.toTimestamp(d.date), Number(d.value)] as number[])
      .filter((d: number[]) => Number.isFinite(d[0]) && Number.isFinite(d[1]));

    if (this.dataStart !== null && this.dataEnd !== null) {
      conv = conv.filter(
        (d) => d[0] >= this.dataStart! && d[0] <= this.dataEnd!,
      );
    }
    conv.sort((a: number[], b: number[]) => a[0] - b[0]);
    if (!conv.length) return null;

    const benchValue = conv[0][1];
    if (!benchValue) return null;
    conv = conv.map((d) => [d[0], +((d[1] / benchValue - 1) * 100).toFixed(4)]);

    return {
      name: this.benchmark.index_name_short ?? "Benchmark",
      data: conv,
      type: "line",
      color: this.benchmarkColor,
      lineWidth: 2,
      zIndex: 1,
      marker: { enabled: false },
    };
  }

  private toTimestamp(raw: any): number {
    if (raw instanceof Date) return raw.getTime();
    if (typeof raw === "number" && Number.isFinite(raw)) return raw;
    if (typeof raw === "string") {
      const parsed = Date.parse(raw);
      if (!isNaN(parsed)) return parsed;
    }
    return NaN;
  }

  private initChart(): void {
    let highlight: any = null;
    if (this.highlightStart && this.highlightEnd) {
      highlight = {
        color: "rgba(124,92,255,0.10)",
        from: this.highlightStart,
        to: this.highlightEnd,
      };
    }

    const buttons = this.getAvailableRangeButtons();

    this.chartOptions = {
      chart: {
        height: this.isPrint ? 460 : (this.height as any),
        backgroundColor: "transparent",
        spacing: [12, 4, 8, 4],
        style: { fontFamily: "Inter, system-ui, sans-serif" },
        zooming: { mouseWheel: { enabled: false } },
        resetZoomButton: { position: { x: 0, y: -28 } },
      },
      credits: { enabled: false },
      title: { text: "" },
      navigator: { enabled: false, maskInside: false },
      scrollbar: { enabled: false },
      rangeSelector: {
        ...HC_RANGE_SELECTOR,
        buttons,
        selected: buttons.length - 1,
      },
      xAxis: {
        type: "datetime",
        lineColor: "transparent",
        tickColor: "transparent",
        plotBands: highlight ? [highlight] : undefined,
        crosshair: {
          color: "rgba(124,92,255,0.35)",
          width: 1,
          dashStyle: "ShortDot",
          label: { enabled: false },
        },
        labels: { style: { color: "var(--text-dim)" } },
        events: {
          afterSetExtremes: (e: any) => this.rebaseToWindow(e.min, e.max),
        },
      },
      yAxis: [
        {
          opposite: false,
          gridLineColor: "var(--border)",
          gridLineDashStyle: "ShortDash",
          gridLineWidth: 1,
          minorGridLineWidth: 0,
          title: { text: "" },
          labels: { format: "{text}%", style: { color: "var(--text-dim)" } },
        },
      ],
      legend: {
        enabled: true,
        align: "left",
        verticalAlign: "bottom",
        layout: "horizontal",
        itemDistance: 18,
        margin: 12,
        padding: 0,
        symbolRadius: 2,
        symbolHeight: 3,
        symbolWidth: 14,
        squareSymbol: false,
        itemStyle: {
          color: "#8A90A3",
          fontFamily: "Inter, system-ui, sans-serif",
          fontWeight: "500",
          fontSize: "11.5px",
        },
        itemHoverStyle: { color: "#E8EAF0" },
        itemHiddenStyle: { color: "#5A6076" },
      },
      tooltip: {
        // Stock charts default to `outside: true`, which appends the tooltip
        // container directly to <body>, escaping `.experience-scope` and the
        // scoped tooltip styles. Keep it inside the chart so theming applies.
        outside: false,
        borderRadius: 8,
        borderWidth: 1,
        padding: 10,
        shadow: false,
        style: { color: "var(--text)" },
        shared: true,
        useHTML: true,
        formatter(this: any) {
          const points = this.points as any[];
          const date = moment(this.x).format("YYYY-MM-DD");
          const rows = points
            .map(
              (p) =>
                `<div style="display:flex;align-items:center;gap:10px;margin-top:4px;">
                   <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${p.color};flex:none;"></span>
                   <span style="color:var(--text-dim);">${p.series.name}</span>
                   <span style="margin-left:auto;font-family:var(--font-mono);font-variant-numeric:tabular-nums;font-weight:600;color:var(--text);">${p.y.toFixed(2)}%</span>
                 </div>`,
            )
            .join("");
          return `<div style="min-width:200px;font-size:12px;color:var(--text);"><div style="font-size:10.5px;letter-spacing:0.06em;color:var(--text-dim);text-transform:uppercase;margin-bottom:4px;font-weight:500;">${date}</div>${rows}</div>`;
        },
      },
      plotOptions: {
        series: {
          lineWidth: 2,
          dataGrouping: { enabled: false },
          marker: {
            enabled: false,
            states: { hover: { enabled: true, radius: 4 } },
          },
          states: { hover: { lineWidthPlus: 0 } },
          label: { enabled: false },
        },
        area: {
          fillOpacity: 1,
          lineWidth: 2,
        },
      },
      series: this.series as any,
    };

    setTimeout(() => {
      this.loaded.set(true);
    });
  }

  private rebaseToWindow(min: number, max: number): void {
    if (
      this.isRebasing ||
      !this.chartInstance ||
      !this.originalSeriesData.length
    )
      return;
    this.isRebasing = true;
    try {
      for (let i = 0; i < this.originalSeriesData.length; i++) {
        const original = this.originalSeriesData[i];
        const firstVisible = original.find((d) => d[0] >= min);
        const baseValue = firstVisible ? 100 + firstVisible[1] : 100;
        const rebased = original.map((d) => [
          d[0],
          +(((100 + d[1]) / baseValue - 1) * 100).toFixed(2),
        ]);
        if (this.chartInstance.series[i]) {
          this.chartInstance.series[i].setData(rebased, false, false, false);
        }
      }
      this.chartInstance.redraw(false);
    } finally {
      this.isRebasing = false;
    }
  }

  private getAvailableRangeButtons(): any[] {
    const allButtons = [
      { type: "month", count: 1, text: "1M", title: "View 1 month" },
      { type: "month", count: 3, text: "3M", title: "View 3 months" },
      { type: "month", count: 6, text: "6M", title: "View 6 months" },
      { type: "ytd", text: "YTD", title: "View year to date" },
      { type: "year", count: 1, text: "1Y", title: "View 1 year" },
      { type: "year", count: 3, text: "3Y", title: "View 3 years" },
      { type: "year", count: 5, text: "5Y", title: "View 5 years" },
      { type: "year", count: 10, text: "10Y", title: "View 10 years" },
      { type: "all", text: "ALL", title: "View all" },
    ];

    if (!this.dataStart || !this.dataEnd) return allButtons;

    const dataSpanMs = this.dataEnd - this.dataStart;
    const oneMonthMs = 30.44 * 24 * 60 * 60 * 1000;
    const oneYearMs = 365.25 * 24 * 60 * 60 * 1000;

    return allButtons.filter((b: any) => {
      if (b.type === "all" || b.type === "ytd") return true;
      if (b.type === "month") return dataSpanMs >= b.count * oneMonthMs;
      if (b.type === "year") return dataSpanMs >= b.count * oneYearMs;
      return true;
    });
  }
}
